import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { base_url as base_url_from_axios_config } from "../../utils/AxiosConfig";
import dayjs from "dayjs";
import { FaSearch } from "react-icons/fa";

const FleetRegistrationExpiry = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const storedUserDataArray =
    JSON.parse(localStorage.getItem("BondTimeuserDataArray")) || [];
  const companyCode = storedUserDataArray[0]?.cmpcode || null;

  const [base_url] = useState(base_url_from_axios_config);

  const fetchRegistrationData = async () => {
    try {
      setLoading(true);

      const res = await axios.get(
        `${base_url}/api/cpayscount/${companyCode}/FLEETREGISTRATION/-/active`,
      );

      setData(res.data || []);
    } catch (error) {
      console.error("Error fetching fleet registration data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRegistrationData();
  }, []);

  const processedData = useMemo(() => {
    return (data || [])
      .filter((item) => {
        // Filter out invalid entries
        if (!item?.Reg_No?.trim()) return false;

        // Filter out placeholder entries
        if (
          item.Reg_No.toLowerCase().includes("vehicle reg no") ||
          item.Make?.toLowerCase() === "make"
        ) {
          return false;
        }

        // Filter out entries with invalid expiry date
        if (
          !item?.ExpiryDate ||
          item.ExpiryDate.startsWith("1900-01-01")
        ) {
          return false;
        }

        return true;
      })
      .map((item) => {
        const expiryDate = new Date(item.ExpiryDate);
        const today = new Date();

        const diffTime = expiryDate - today;
        const daysLeft = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        return {
          ...item,
          expiryDate,
          daysLeft,
        };
      })
      .sort((a, b) => a.daysLeft - b.daysLeft);
  }, [data]);

  const getStatus = (daysLeft) => {
    if (daysLeft < 0)
      return {
        text: "Expired",
        className: "bg-red-100 text-red-700",
      };

    if (daysLeft <= 30)
      return {
        text: "Expiring Soon",
        className: "bg-yellow-100 text-yellow-700",
      };

    return {
      text: "Active",
      className: "bg-green-100 text-green-700",
    };
  };

  const filteredData = useMemo(() => {
    return processedData.filter(
      (item) =>
        item?.Reg_No?.toLowerCase().includes(search.toLowerCase()) ||
        item?.Make?.toLowerCase().includes(search.toLowerCase()),
    );
  }, [processedData, search]);

  const expiredCount = processedData.filter((x) => x.daysLeft < 0).length;

  const expiringSoonCount = processedData.filter(
    (x) => x.daysLeft >= 0 && x.daysLeft <= 30,
  ).length;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center">
          <div className="w-1 h-8 bg-gradient-to-r from-[#2663EB] to-blue-400 rounded-full mr-2"></div>

          <h6 className="text-xl font-bold text-gray-900">
            <span className="bg-gradient-to-r from-[#2663EB] to-blue-600 bg-clip-text text-transparent">
              FLEET REGISTRATION EXPIRY
            </span>
          </h6>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className="bg-white rounded-lg border p-2">
          <p className="text-gray-500 text-sm">Total Vehicles</p>
          <h2 className="text-2xl font-bold text-[#2663EB]">
            {processedData.length}
          </h2>
        </div>

        <div className="bg-white rounded-lg border p-2">
          <p className="text-gray-500 text-sm">Expired</p>
          <h2 className="text-2xl font-bold text-red-600">{expiredCount}</h2>
        </div>

        <div className="bg-white rounded-lg border p-2">
          <p className="text-gray-500 text-sm">Expiring Within 30 Days</p>
          <h2 className="text-2xl font-bold text-yellow-500">
            {expiringSoonCount}
          </h2>
        </div>
      </div>

      <div className="bg-white w-[350px] flex items-center gap-2 p-2 border border-gray-300 rounded-md mb-3">
        <FaSearch className="text-gray-400" />

        <input
          type="text"
          placeholder="Search Make, Reg No..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full outline-none text-sm"
        />
      </div>

      {loading ? (
        <div className="p-8 text-center text-gray-500">Loading...</div>
      ) : processedData.length === 0 ? (
        <div className="p-8 text-center text-gray-500">No records found</div>
      ) : (
        <div className="overflow-x-auto">
          <div className="overflow-auto max-h-[65vh] border scrollbar-none bg-white rounded-md">
            <table className="min-w-full border-separate border-spacing-0">
              <thead className="sticky top-0 z-10">
                <tr className="bg-[#2663EB] text-white text-sm">
                  <th className="px-3 py-2 text-left">Sl No</th>
                  <th className="px-3 py-2 text-left">Reg No</th>
                  <th className="px-3 py-2 text-left">Make</th>
                  <th className="px-3 py-2 text-left">Expiry Date</th>
                  <th className="px-3 py-2 text-left">Days Left</th>
                  <th className="px-3 py-2 text-left">Status</th>
                </tr>
              </thead>

              <tbody>
                {filteredData.map((item, index) => (
                  <tr
                    key={index}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-3 py-2 border">{index + 1}</td>

                    <td className="px-3 py-2 border font-medium">
                      {item.Reg_No}
                    </td>

                    <td className="px-3 py-2 border">{item.Make || "-"}</td>

                    <td className="px-3 py-2 border">
                      {dayjs(item.ExpiryDate).format("DD-MMM-YYYY")}
                    </td>

                    <td className="px-3 py-2 border">{item.daysLeft}</td>

                    <td className="px-3 py-2 border">
                      {item.daysLeft < 0 ? (
                        <span className="px-2 py-1 rounded-full bg-red-100 text-red-700 text-xs font-semibold">
                          Expired
                        </span>
                      ) : item.daysLeft <= 30 ? (
                        <span className="px-2 py-1 rounded-full bg-yellow-100 text-yellow-700 text-xs font-semibold">
                          Expiring Soon
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs font-semibold">
                          Active
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default FleetRegistrationExpiry;